// A generic, registry-driven "chat component" seam for the SmartNote assistant.
//
// The assistant emits typed suggestions; a *component descriptor* here declares how one type is
// rendered, inline-edited and committed. Adding a new interactive card in future — a task, a
// question the assistant asks, a different way to accept a suggestion — is one registry entry,
// not another bespoke branch in the modal. SuggestionComponentCard renders any descriptor; the
// modal falls back to the legacy DraftSuggestionCard for types with no descriptor, so existing
// cards are untouched.
//
// Descriptor contract (kept deliberately small — extend it as new components need to):
//   type            the signal type this component renders
//   title           the small header label shown above the card body
//   buttonLabel     the accept button's text
//   stepType        the action step whose payload the fields seed from and amend onto
//   fields          the inline editable form: [{ key, label, input, payloadKey, placeholder? }].
//                   `input` is an INPUT_KIND ("text" | "textarea" | "date"); a future question
//                   component adds "select"/"radio" here and nothing else changes.
//   seedValues      (suggestion) -> initial field values (defaults to reading the step payload)
//   buildAmendments (suggestion, values) -> { [stepId]: fullReplacementPayload } for the accept
//   onAccepted      ({ queryClient, suggestion }) -> side effects after a successful accept
//
// Amendments always REPLACE a step's whole payload (the action-service validates it against the
// step schema with additionalProperties:false), so a replacement is built by overlaying edits
// onto a clone of the original payload — never by inventing keys.

import { CheckSquare } from "react-bootstrap-icons";

export const INPUT_KINDS = { TEXT: "text", TEXTAREA: "textarea", DATETIME: "datetime", ASSIGNEES: "assignees" };

const TASK_STEP_TYPE = "calendar.task.create";

// An ISO instant -> the "yyyy-MM-ddTHH:mm" a datetime-local input edits, in the viewer's LOCAL time —
// the same instant the calendar renders. Slicing the raw ISO instead would print it in whatever offset
// the backend serialised (UTC), reading an hour behind the calendar during BST.
function isoToDateTimeLocal(iso) {
  if (typeof iso !== "string" || !iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// Put an edited "yyyy-MM-ddTHH:mm" (local wall-clock) back into due_at as an absolute instant. The
// field is local time, so interpret it in local time and serialise the instant — the backend and the
// calendar both key off the instant, not the printed offset, so this round-trips the same moment.
function withDateTime(originalIso, dtLocal) {
  if (!dtLocal) return originalIso;
  const date = new Date(dtLocal);
  return Number.isNaN(date.getTime()) ? originalIso : date.toISOString();
}

// The read-only sentence the card shows before/after editing, e.g. "Call Rob on Friday, 07 Jul at
// 2pm". Composed from the edited values so it always reflects what will be created.
function taskSentence(values) {
  const title = (values.title ?? "").trim();
  const when = formatWhen(values.dueAt);
  return when ? `${title} on ${when}` : title;
}

// "2026-07-07T14:00" -> "Friday, 07 Jul at 2pm" (drops ":00" on the hour, like the design).
function formatWhen(dtLocal) {
  if (!dtLocal) return "";
  const date = new Date(dtLocal);
  if (Number.isNaN(date.getTime())) return "";
  const day = date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
  });
  const minutes = date.getMinutes();
  const hour = date.getHours() % 12 || 12;
  const suffix = date.getHours() < 12 ? "am" : "pm";
  const time = minutes === 0 ? `${hour}${suffix}` : `${hour}:${String(minutes).padStart(2, "0")}${suffix}`;
  return `${day} at ${time}`;
}

// The "create a task" component: an editable title, due date/time and — mirroring a note's tagging —
// the colleagues it's assigned to, edited via the same NoteTagEditor. On accept the final assignee list
// is sent to the task-accept endpoint, which fans the task out (one per colleague). The customer is
// stamped in code by the intelligence-service and isn't editable here.
const taskComponent = {
  type: "TaskRequested",
  title: "Create a task",
  buttonLabel: "Create",
  successMessage: "Task created",
  stepType: TASK_STEP_TYPE,
  // Task acceptance recomposes the fan-out server-side rather than amending pre-created steps, so the
  // card sends the final values to a dedicated endpoint (see api/intelligenceController acceptTask).
  acceptVia: "task",
  // The neutral slate checkbox identity tasks carry everywhere else (calendar chips, activity feed,
  // detail popup) — so the suggestion card reads as the same thing, not a generic AI card.
  Icon: CheckSquare,
  iconTile: "bg-slate-100 text-slate-600",
  summary: taskSentence,
  fields: [
    {
      key: "title",
      label: "Task",
      input: INPUT_KINDS.TEXT,
      payloadKey: "title",
      placeholder: "What needs doing?",
      grow: true,
      autoFocus: true,
    },
    { key: "dueAt", label: "When", input: INPUT_KINDS.DATETIME, payloadKey: "due_at" },
    { key: "assignees", label: "Assign to", input: INPUT_KINDS.ASSIGNEES, addLabel: "Assign colleague", required: false },
  ],
  seedValues(suggestion) {
    // Fan-out means several task steps; they share the title/due, so read the first. The assignees the
    // card edits ride the signal params (id + name), the same shape NoteTagEditor emits.
    const steps = (suggestion?.params?.steps ?? []).filter((s) => s.stepType === TASK_STEP_TYPE);
    const payload = steps[0]?.payload ?? {};
    return {
      title: payload.title ?? "",
      dueAt: isoToDateTimeLocal(payload.due_at),
      assignees: suggestion?.params?.assignees ?? [],
    };
  },
  // The task-accept request: the final assignee ids (BFF re-validates them) plus the edited title/due.
  buildAcceptRequest(values) {
    return {
      assigneeUserIds: (values.assignees ?? []).map((assignee) => assignee.id),
      title: typeof values.title === "string" ? values.title.trim() : undefined,
      dueAt: values.dueAt ? withDateTime(undefined, values.dueAt) : undefined,
    };
  },
  onAccepted({ queryClient }) {
    // The created task lands in Today's tasks (and the sidebar count badge) via the shared
    // ["todaysTasks"] query — refresh it so the new task shows without a reload.
    queryClient.invalidateQueries({ queryKey: ["todaysTasks"] });
  },
};

const REGISTRY = { [taskComponent.type]: taskComponent };

// The descriptor for a suggestion type, or null when it has no generic component (the modal then
// renders the legacy DraftSuggestionCard).
export function getSuggestionComponent(type) {
  return REGISTRY[type] ?? null;
}

// Whether a suggestion is decidable: the action-service pre-created it (actionId present) and the
// approval window hasn't lapsed. Mirrors DraftSuggestionCard's gate so a stale card can't be sent.
export function isComponentDecidable(suggestion) {
  const params = suggestion?.params ?? {};
  if (!params.actionId) return false;
  return !params.expiresAt || new Date(params.expiresAt) > new Date();
}
